import styled from "@emotion/styled";
import React from "react";
import useOpen from "../../../hooks/useOpen";
import { DutySheetTask, PlanOfCareItemId } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { AppButton } from "../../Generic/AppButton";
import { AppCheckbox } from "../../Generic/AppCheckbox";
import { AppListItem } from "../../Generic/AppList";
import Flex from "../../Generic/Flex";
import { If } from "../../Generic/If";
import { isDefined } from "../../Generic/IsDefined";
import Textarea from "../../Generic/Textarea/Textarea";

interface DutyTaskItemProps {
  duty: DutySheetTask;
  notes: string | null;
  isChecked: boolean;
  isTaskItemDisabled?: boolean;
  isRequired?: boolean;
  onChangeNotes: (id: PlanOfCareItemId, notes: string) => void;
  onCheck: (id: PlanOfCareItemId, isChecked: boolean) => void;
  onDisabledTaskItemClick?: () => void;
}

function DutyTaskItem(props: DutyTaskItemProps) {
  const { isOpen: isNotesOpen, actions } = useOpen({ initialState: props.notes !== null });

  const handleClick = () => {
    props.isTaskItemDisabled === true && isDefined(props.onDisabledTaskItemClick)
      ? props.onDisabledTaskItemClick()
      : props.onCheck(props.duty.id, !props.isChecked);
  };

  const handleChangeNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.onChangeNotes(props.duty.id, e.target.value);
  };

  const notesButton = (
    <AppButton size="small" variant="outline" onClick={actions.toggle}>
      <If condition={!isNotesOpen}>+ {translate(STRINGS.NOTES)}</If>
      <If condition={isNotesOpen}>- {translate(STRINGS.NOTES)}</If>
    </AppButton>
  );

  return (
    <DutyTaskItem.Root>
      <AppListItem
        key={`task-${props.duty.id}`}
        leading={
          <AppCheckbox
            checked={props.isChecked}
            onClick={handleClick}
            disabled={props.isTaskItemDisabled}
          />
        }
        title={
          <span>
            {props.duty.name} ({props.duty.code})
          </span>
        }
        trailing={
          <Flex direction="row">
            <If condition={props.isRequired === true && !props.isChecked}>
              <DutyTaskItem.Badge>{translate(STRINGS.REQUIRED)}</DutyTaskItem.Badge>
            </If>
            <If condition={props.isChecked && props.notes === null}>{notesButton}</If>
          </Flex>
        }
        extra={
          isNotesOpen && props.isChecked ? (
            <Textarea
              value={props.notes ?? ""}
              onChange={handleChangeNotes}
              disabled={props.isTaskItemDisabled}
            />
          ) : null
        }
        onClickTitle={handleClick}
      />
    </DutyTaskItem.Root>
  );
}

DutyTaskItem.Root = styled.div`
  & > .title {
    padding: 12px 0;
  }

  min-height: 56px;
`;

DutyTaskItem.Badge = styled.div`
  font-size: 14px;
  background-color: #feeeee;
  color: #f05757;
  border-radius: 4px;
  padding: 2px 4px;
  margin-inline-start: 8px;
  align-self: flex-start;
  margin-inline-end: 7px;
`;

export default DutyTaskItem;
